import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import { Users, Plus, LogOut, Dumbbell, Eye, Trash2, X } from 'lucide-react';

const AdminDashboard = () => {
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showPlanForm, setShowPlanForm] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', goal: 'fat_loss', current_weight: '', target_weight: '', daily_calorie_target: '', training_frequency: '' });
  const [planForm, setPlanForm] = useState({ title: '', description: '', week_start: new Date().toISOString().split('T')[0], exercises: [{ name: '', sets: '', reps: '', notes: '' }] });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!user || user.role !== 'admin') { navigate('/portal'); return; }
    loadClients();
  }, [user]);

  const loadClients = async () => {
    try {
      const res = await api.get('/api/admin/clients');
      setClients(res.data);
    } catch (e) { console.error(e); }
  };

  const createClient = async (e) => {
    e.preventDefault();
    setError('');
    try {
      const payload = { ...form, current_weight: form.current_weight ? parseFloat(form.current_weight) : null, target_weight: form.target_weight ? parseFloat(form.target_weight) : null, daily_calorie_target: form.daily_calorie_target ? parseInt(form.daily_calorie_target) : null, training_frequency: form.training_frequency ? parseInt(form.training_frequency) : null };
      await api.post('/api/admin/clients', payload);
      setSuccess('Client created!');
      setShowCreate(false);
      setForm({ name: '', email: '', password: '', goal: 'fat_loss', current_weight: '', target_weight: '', daily_calorie_target: '', training_frequency: '' });
      loadClients();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create client');
    }
  };

  const deleteClient = async (id) => {
    if (!window.confirm('Delete this client and all their data?')) return;
    try {
      await api.delete(`/api/admin/clients/${id}`);
      loadClients();
      if (selectedClient?.id === id) setSelectedClient(null);
    } catch (e) { console.error(e); }
  };

  const addExercise = () => setPlanForm(p => ({ ...p, exercises: [...p.exercises, { name: '', sets: '', reps: '', notes: '' }] }));
  const updateExercise = (i, field, val) => setPlanForm(p => ({ ...p, exercises: p.exercises.map((ex, idx) => idx === i ? { ...ex, [field]: val } : ex) }));
  const removeExercise = (i) => setPlanForm(p => ({ ...p, exercises: p.exercises.filter((_, idx) => idx !== i) }));

  const submitPlan = async (e) => {
    e.preventDefault();
    if (!selectedClient) return;
    try {
      await api.post(`/api/admin/clients/${selectedClient.id}/training-plan`, planForm);
      setSuccess('Training plan uploaded!');
      setShowPlanForm(false);
      setPlanForm({ title: '', description: '', week_start: new Date().toISOString().split('T')[0], exercises: [{ name: '', sets: '', reps: '', notes: '' }] });
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create plan');
    }
  };

  const handleLogout = () => { logout(); navigate('/portal'); };

  const goalLabels = { fat_loss: 'Fat Loss', muscle_gain: 'Muscle Gain', maintenance: 'Maintenance' };

  return (
    <div className="portal-page" data-testid="admin-dashboard">
      <div className="portal-topbar">
        <h2><Dumbbell size={20} /> Admin Dashboard</h2>
        <div className="portal-topbar-right">
          <span>Hi, {user?.name}</span>
          <button onClick={handleLogout} className="portal-btn-ghost" data-testid="logout-btn"><LogOut size={16} /> Logout</button>
        </div>
      </div>

      <div className="portal-content">
        {success && <div className="portal-success">{success}</div>}
        {error && <div className="portal-error">{error}</div>}

        <div className="portal-section-header">
          <h3><Users size={18} /> Clients ({clients.length})</h3>
          <button onClick={() => setShowCreate(!showCreate)} className="portal-btn-primary" data-testid="create-client-btn">
            <Plus size={16} /> Add Client
          </button>
        </div>

        {showCreate && (
          <form onSubmit={createClient} className="portal-form-card" data-testid="create-client-form">
            <h4>New Client</h4>
            <div className="portal-form-grid">
              <div className="portal-field"><label>Name</label><input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required data-testid="client-name" /></div>
              <div className="portal-field"><label>Email</label><input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} required data-testid="client-email" /></div>
              <div className="portal-field"><label>Password</label><input type="text" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} required data-testid="client-password" /></div>
              <div className="portal-field"><label>Goal</label>
                <select value={form.goal} onChange={e => setForm(f => ({ ...f, goal: e.target.value }))} data-testid="client-goal">
                  <option value="fat_loss">Fat Loss</option>
                  <option value="muscle_gain">Muscle Gain</option>
                  <option value="maintenance">Maintenance</option>
                </select>
              </div>
              <div className="portal-field"><label>Current Weight (kg)</label><input type="number" step="0.1" value={form.current_weight} onChange={e => setForm(f => ({ ...f, current_weight: e.target.value }))} /></div>
              <div className="portal-field"><label>Target Weight (kg)</label><input type="number" step="0.1" value={form.target_weight} onChange={e => setForm(f => ({ ...f, target_weight: e.target.value }))} /></div>
              <div className="portal-field"><label>Daily Calorie Target</label><input type="number" value={form.daily_calorie_target} onChange={e => setForm(f => ({ ...f, daily_calorie_target: e.target.value }))} /></div>
              <div className="portal-field"><label>Training Frequency (per week)</label><input type="number" value={form.training_frequency} onChange={e => setForm(f => ({ ...f, training_frequency: e.target.value }))} /></div>
            </div>
            <button type="submit" className="portal-btn-primary">Create Client</button>
          </form>
        )}

        <div className="portal-clients-grid">
          {clients.map(c => (
            <div key={c.id} className={`portal-client-card ${selectedClient?.id === c.id ? 'active' : ''}`} data-testid={`client-card-${c.id}`}>
              <div className="portal-client-info">
                <h4>{c.name}</h4>
                <p className="portal-muted">{c.email}</p>
                <span className={`portal-badge portal-badge-${c.goal}`}>{goalLabels[c.goal] || c.goal}</span>
                {c.current_weight && <p className="portal-stat">{c.current_weight}kg → {c.target_weight || '?'}kg</p>}
                {c.daily_calorie_target && <p className="portal-stat">{c.daily_calorie_target} kcal/day target</p>}
              </div>
              <div className="portal-client-actions">
                <button onClick={() => { setSelectedClient(c); setShowPlanForm(true); }} className="portal-btn-sm"><Dumbbell size={14} /> Add Plan</button>
                <button onClick={() => deleteClient(c.id)} className="portal-btn-sm portal-btn-danger"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
          {clients.length === 0 && <p className="portal-muted">No clients yet. Add your first client above.</p>}
        </div>

        {showPlanForm && selectedClient && (
          <div className="portal-modal-overlay" onClick={() => setShowPlanForm(false)}>
            <div className="portal-modal" onClick={e => e.stopPropagation()} data-testid="training-plan-form">
              <div className="portal-modal-header">
                <h3>Training Plan for {selectedClient.name}</h3>
                <button onClick={() => setShowPlanForm(false)} className="portal-btn-ghost"><X size={18} /></button>
              </div>
              <form onSubmit={submitPlan}>
                <div className="portal-field"><label>Plan Title</label><input value={planForm.title} onChange={e => setPlanForm(p => ({ ...p, title: e.target.value }))} required placeholder="e.g. Week 12 - Upper/Lower Split" /></div>
                <div className="portal-field"><label>Description</label><textarea value={planForm.description} onChange={e => setPlanForm(p => ({ ...p, description: e.target.value }))} placeholder="Optional notes..." rows={2} /></div>
                <div className="portal-field"><label>Week Starting</label><input type="date" value={planForm.week_start} onChange={e => setPlanForm(p => ({ ...p, week_start: e.target.value }))} required /></div>
                <h4 style={{ margin: '1rem 0 0.5rem' }}>Exercises</h4>
                {planForm.exercises.map((ex, i) => (
                  <div key={i} className="portal-exercise-row">
                    <input placeholder="Exercise name" value={ex.name} onChange={e => updateExercise(i, 'name', e.target.value)} required />
                    <input placeholder="Sets" type="number" value={ex.sets} onChange={e => updateExercise(i, 'sets', e.target.value)} style={{ width: '70px' }} />
                    <input placeholder="Reps" value={ex.reps} onChange={e => updateExercise(i, 'reps', e.target.value)} style={{ width: '70px' }} />
                    <input placeholder="Notes" value={ex.notes} onChange={e => updateExercise(i, 'notes', e.target.value)} />
                    {planForm.exercises.length > 1 && <button type="button" onClick={() => removeExercise(i)} className="portal-btn-ghost"><X size={14} /></button>}
                  </div>
                ))}
                <button type="button" onClick={addExercise} className="portal-btn-ghost" style={{ marginTop: '0.5rem' }}><Plus size={14} /> Add Exercise</button>
                <button type="submit" className="portal-btn-primary" style={{ marginTop: '1rem', width: '100%' }}>Upload Training Plan</button>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
